import { useCallback, useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';

import { HackIcon } from './HackIcon';
import { arrowButtonSx, boxSx, rootSx } from './styles';

import type { SxProps, Theme } from '@mui/material';
import type { FC } from 'react';

export interface Day {
  date: string;
  dateFormated: string;
  weekDay: string;
  disabled: boolean;
}

export interface DayPickerV2Props {
  days: Day[];
  /** Value of selected date. */
  selectedDate: string | null;
  /** Overriding styles for DayPicker wrapper. */
  wrapperSx?: SxProps<Theme>;
  /** Overriding styles for DayPicker list item. */
  itemSx?: SxProps<Theme>;
  /** if `true` selected date will stick to the edges of container. */
  sticky?: boolean;
  /** Fired when the date is changed. */
  onChange: (newDate: string) => void;
  /** Overriding styles for DayPicker disabled overlay. */
  disabledSx?: SxProps<Theme>;
  disabled?: boolean;
}

const DayPickerV2: FC<DayPickerV2Props> = ({
  days,
  selectedDate = new Date(),
  wrapperSx,
  itemSx,
  onChange,
  sticky = true,
  disabled = false,
}) => {
  const isDesktop = useMediaQuery<Theme>(({ breakpoints }) => breakpoints.up('md'));
  const [{ isDesktopNext, isPrevVisible }, setState] = useState({
    isDesktopNext: false,
    isPrevVisible: false,
  });

  const hackPrev = useCallback(
    (isVisible: boolean) => () => {
      setState((prevState) => ({
        ...prevState,
        isPrevVisible: isVisible,
      }));
    },
    [],
  );

  const hackNext = useCallback(
    (isVisible: boolean) => () => {
      setState((prevState) => ({
        ...prevState,
        isDesktopNext: isVisible,
      }));
    },
    [],
  );

  const nextIconProps = useMemo(() => ({ onMount: hackNext(true) }), [hackNext]);

  if (isDesktop) {
    return (
      <Box sx={rootSx(isPrevVisible, disabled, wrapperSx)}>
        <ScrollingCarousel
          leftIcon={
            <Box sx={arrowButtonSx}>
              <HackIcon
                icon="long-arrow-prev"
                onUnmount={hackPrev(false)}
                onMount={hackPrev(true)}
              />
            </Box>
          }
          rightIcon={
            <Box sx={arrowButtonSx}>
              <HackIcon icon="long-arrow-next" onUnmount={hackNext(false)} {...nextIconProps} />
            </Box>
          }
          className="carousel"
        >
          {days.map(({ date, dateFormated, weekDay, disabled: isDisabled }) => {
            const isSelected = date === selectedDate;

            return (
              <Box
                key={date}
                aria-label={`${date} ${weekDay}`}
                data-testid={`day-${date}-${weekDay}`}
                onClick={() => onChange(date)}
                sx={boxSx(isSelected, isPrevVisible, sticky, itemSx, isDesktopNext, isDisabled)}
              >
                <Typography noWrap>
                  <b>{dateFormated}</b>
                </Typography>
                <Typography>{weekDay}</Typography>
              </Box>
            );
          })}
        </ScrollingCarousel>
      </Box>
    );
  }

  return (
    <Box sx={rootSx(false, disabled, wrapperSx)}>
      {days.map(({ date, dateFormated, weekDay, disabled: isDisabled }) => {
        const isSelected = date === selectedDate;

        return (
          <Box
            key={date}
            aria-label={`${date} ${weekDay}`}
            data-testid={`day-${date}-${weekDay}`}
            onClick={() => onChange(date)}
            sx={boxSx(isSelected, false, sticky, itemSx, false, isDisabled)}
          >
            <Typography noWrap>
              <b>{dateFormated}</b>
            </Typography>
            <Typography>{weekDay}</Typography>
          </Box>
        );
      })}
      <Box></Box>
    </Box>
  );
};

export default DayPickerV2;
